import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, PartPurchaseRepository, UtilityService } from 'app/core/core.module';
import { Part } from 'app/core/model/entity-model';
import { PartDialogComponent } from 'app/shared/part-dialog/part-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { PartPurchaseOrderDialogComponent } from '../part-po-dialog/part-po-dialog.component';

@Component({
    selector: 'app-part-inventory',
    templateUrl: './part-inventory.component.html',
    styleUrls: ['./part-inventory.component.scss'],
    standalone: false
})
export class PartInventoryComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    parts: any;
    orders: any[];
    filter: any;
    isToastVisible = false;
    processing = false;
    showAsDialog = false;
    preferredVendor: string;
    selectedPartIds = [];
    orderLinePartsOnNew: any;
    showAllVendors: boolean = false;

    canSeeCost = false;

    @ViewChild('partsGrid') partsGrid: DxDataGridComponent;
    onOptionChangedShared = SharedModule.onOptionChanged;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        private utility: UtilityService,
        private partPurchaseRepository: PartPurchaseRepository
    ) {}

    async ngOnInit() {
        await this.getData();
        await this.getInventoryAdjustments();
        await this.getOrderLineDetailParts();
        this.canSeeCost = this.auth.hasRole('Parts:Admin') && !this.showAsDialog;
    }

    async getData() {
        let parts = null;

        parts = (await this.uow.getPartInventory()) as Part[];
        parts.forEach((part) => {
            let qty = 0;
            part.childrenParts.forEach(child => {
                    qty += child.onOrder;
            }) ?? 0;
            part.childrenOnOrder = qty;
        });

        if (!!this.showAsDialog && !this.showAllVendors) {
            parts = parts.filter((x) => {
                if (x.preferredVendor === this.preferredVendor) return x;
            });
        }

        this.parts = parts;
    }

    async toggleVendor() {
        this.showAllVendors = !this.showAllVendors;
        await this.getData();
    }

    async getInventoryAdjustments() {
        const adjustments = await this.utility.getInventoryAdjustments();
        this.parts.forEach((part) => {
            const adjustment = _.find(adjustments, { Key: part.id }) as any;
            if (!!adjustment) {
                const partAdjustment = adjustment.PartAdjustment;
                const adjustmentDate = moment(partAdjustment.Date).format('MM/DD/YYYY');
                part.lastAdjustment = `${adjustmentDate} - ${partAdjustment.CreatedBy} - Reason: ${partAdjustment.Reason}`;
            }
        });
    }

    async getOrderLineDetailParts() {
        const orderLinePartsNew = await this.uow.getOrderLineDetailPartsOnNew();
        this.orderLinePartsOnNew = orderLinePartsNew;
        this.parts.forEach((part) => {
            const orderLinePartInfo = _.find(orderLinePartsNew, { partId: part.id }) as any;
            part.onOrderNew = !!orderLinePartInfo ? orderLinePartInfo.partCountOnNewStatus : 0;
            part.projectedInventory = part.inStock - part.onOrderNew;
            part.weldShopPI = part.onOrder + (part.inStock-part.onOrderNew);
        });
    }
    async getParentOnOrderQuantities() {
        const partQuantities = await this.uow.getParentOnOrderQuantities();
        this.parts.forEach((part) => {
            const parentLine = _.find(partQuantities, { id: part.id }) as any;
            part.onOrderParent = !!parentLine ? parentLine.totalOnOrder : 0;
        });
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    async selectionChange(e) {
        // console.log(e);
    }

    async rowPrepare(e) {
        if (e.rowType === 'data') {
            if (e.data.weldShopPI < 0) {
                e.rowElement.className = e.rowElement.className + ' zero-inventory';
            }
            if (e.data.weldShopPI < e.data.reOrderMinimum) {
                e.rowElement.className = e.rowElement.className + ' low-inventory';
            }
        }
    }

    async save() {
        this.isToastVisible = true;
        await this.uow.commit();
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    async addPartPurchase(selectedPart: any) {
        if(await this.partPurchaseRepository.addPartPurchase(selectedPart)){
            this.appInfo.pleaseWaitShow();
            await this.getData();
            await this.getOrderLineDetailParts();
            this.appInfo.pleaseWaitHide();
        }
    }

    async open(partId: number) {
        this.router.navigate(['/parts/' + (partId ? partId : 'new')]);
    }

    async rowDoubleClick(e) {
        if (!e.data || !e.data.id) return;
        await this.open(e.data.id);
    }

    // // this method is used to clear the selection when the user searches for something
    // onOptionChanged(e) {
    //     if (e.fullName === 'searchPanel.text') {
    //         e.component.__searchChanged = true;
    //     }
    //     const filterCheck = _.includes(e.fullName, 'filterValue');
    //     if (filterCheck) {
    //         this.partsGrid.instance.clearSelection();
    //     }
    // }

    // // this method is used to clear the selection when the user searches for something
    // onContentReady(e) {
    //     // if (e.component.__searchChanged) {
    //     //     //__searchChanged is for the search box
    //     //     e.component.__searchChanged = false;
    //     //     this.partsGrid.instance.clearSelection();
    //     // }
    // }

    async select() {
        if (this.partsGrid.instance.getSelectedRowsData().length === 0) return;
        const selectedParts = this.partsGrid.instance.getSelectedRowsData();
        console.log(selectedParts);
        let partsSelected = 0;
        selectedParts.forEach((part) => {
            if (!this.selectedPartIds.includes(part.id)) {
                this.selectedPartIds.push({ quantity: part.inStock, id: part.id });
                partsSelected++;
            } else {
                this.appInfo.notifyToast(`Part ${part.tagNumber} has already been added`, 'warning');
            }
        });
        if (partsSelected > 0) this.appInfo.notifyToast(`${partsSelected} parts have been added`);
        this.closeDialog();
    }

    closeDialog() {
        if (this.showAsDialog) this.result.next({ success: true, selectedParts: this.selectedPartIds });
    }

    cancel() {
        this.result.next({ success: false });
    }

    async purchaseOrderDialog(partId: number) {
        const modalRef = this.modalService.show(PartPurchaseOrderDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                partId: partId,
            },
        });

        modalRef.content.result.subscribe((result) => {
            if (result.success && !!result.value) {
                this.openPurchaseOrder(result.value);
            }
            modalRef.hide();
        });
    }

    async partChildrenDialog(part: Part) {
        const modalRef = this.modalService.show(PartDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                title: 'Children On Order',
                partIds: part.childrenParts.map(cp => cp.id)
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                await this.open(result.value);
            }
            modalRef.hide();
        });
    }

    async openPurchaseOrder(id: number) {
        this.router.navigate(['/purchase-orders/' + id]);
    }
}
